import React, { useState } from "react";
import { toast } from "../../../components/AppToast";
import { createFlipkartTestOrder, getShipmentsByOrderIds } from "./flipkartSandboxClient";
import { mapFlipkartShipmentsToOmsForm } from "./flipkartOmsMapper";

const DEFAULT_SHIPMENT_TYPE = "NORMAL";

/**
 * Create a Flipkart sandbox test order and map the shipment response into OMS fields.
 * Dev: uses /flipkart-sandbox proxy (see src/setupProxy.js). Tokens in the browser are only for local testing.
 */
export default function FlipkartSandboxImport({ onApply }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accessToken, setAccessToken] = useState(
    () => process.env.REACT_APP_FLIPKART_SANDBOX_TOKEN || ""
  );
  const [locationId, setLocationId] = useState("");
  const [listingId, setListingId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [shipmentType, setShipmentType] = useState(DEFAULT_SHIPMENT_TYPE);
  const [pasteJson, setPasteJson] = useState("");

  const applyMapped = (partial) => {
    onApply(partial);
    toast.success("Form fields updated from Flipkart data");
  };

  const handlePasteImport = () => {
    let parsed;
    try {
      parsed = JSON.parse(pasteJson.trim());
    } catch {
      toast.error("Invalid JSON");
      return;
    }
    const mapped = mapFlipkartShipmentsToOmsForm(parsed);
    applyMapped(mapped);
  };

  const handleCreateAndFetch = async (e) => {
    e.preventDefault();
    const token = accessToken.trim();
    if (!token) {
      toast.error("Access token is required (or set REACT_APP_FLIPKART_SANDBOX_TOKEN for dev)");
      return;
    }
    if (!locationId.trim() || !listingId.trim()) {
      toast.error("locationId and listingId are required for sandbox test orders");
      return;
    }

    setBusy(true);
    try {
      const lid = listingId.trim();
      const body = {
        shipmentType,
        locationId: locationId.trim(),
        orderItems: [
          {
            listingId: lid,
            quantity: Number(quantity) || 1,
            subItem: { listingId: lid },
          },
        ],
      };

      const created = await createFlipkartTestOrder(token, body);
      const orderId = created?.orderId || created?.order_id;
      if (!orderId) {
        toast.error("Sandbox create succeeded but no orderId in response — check API response shape");
        setBusy(false);
        return;
      }

      const shipmentsPayload = await getShipmentsByOrderIds(token, orderId);
      const mapped = mapFlipkartShipmentsToOmsForm(shipmentsPayload);
      mapped.marketplaceOrderId = mapped.marketplaceOrderId || orderId;
      applyMapped(mapped);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Flipkart request failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded p-3 mb-4 bg-light">
      {/* <button
        type="button"
        className="btn btn-link p-0 text-decoration-none fw-semibold"
        onClick={() => setExpanded((x) => !x)}
      >
        Flipkart sandbox → OMS {expanded ? "▼" : "▶"}
      </button>
      <p className="small text-muted mb-2">
        Create a{" "}
        <a
          href="https://flipkart.github.io/fk-api-platform-docs/docs/mp-api_versioned/create-test-orders"
          target="_blank"
          rel="noopener noreferrer"
        >
          sandbox test order
        </a>{" "}
        then load shipment fields into this form. Use only in development; production should use a server-side
        integration with OAuth per{" "}
        <a href="https://flipkart.github.io/fk-api-platform-docs/docs/intro" target="_blank" rel="noopener noreferrer">
          Getting Started
        </a>
        .
      </p> */}

      {expanded && (
        <>
          <form onSubmit={handleCreateAndFetch} className="row g-2 align-items-end mb-3">
            <div className="col-12 col-md-6">
              <label className="form-label small mb-0">Access token (Bearer)</label>
              <input
                type="password"
                className="form-control form-control-sm"
                autoComplete="off"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="Sandbox token from OAuth"
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label small mb-0">locationId</label>
              <input
                className="form-control form-control-sm"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                placeholder="Seller location ID"
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label small mb-0">listingId</label>
              <input
                className="form-control form-control-sm"
                value={listingId}
                onChange={(e) => setListingId(e.target.value)}
                placeholder="Active listing ID"
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small mb-0">Qty</label>
              <input
                type="number"
                min={1}
                className="form-control form-control-sm"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small mb-0">shipmentType</label>
              <select
                className="form-select form-select-sm"
                value={shipmentType}
                onChange={(e) => setShipmentType(e.target.value)}
              >
                <option value="NORMAL">NORMAL</option>
                <option value="EXPRESS">EXPRESS</option>
                <option value="SELF">SELF</option>
              </select>
            </div>
            <div className="col-12 col-md-4">
              <button type="submit" className="btn btn-sm btn-outline-primary" disabled={busy}>
                {busy ? "Working…" : "Create test order & load into form"}
              </button>
            </div>
          </form>

          <div>
            <label className="form-label small mb-1">Or paste GET /sellers/v3/shipments JSON (from Postman)</label>
            <textarea
              className="form-control form-control-sm font-monospace mb-2"
              rows={4}
              value={pasteJson}
              onChange={(e) => setPasteJson(e.target.value)}
              placeholder='{ "shipments": [ ... ] }'
            />
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handlePasteImport}>
              Map JSON to form
            </button>
          </div>
        </>
      )}
    </div>
  );
}
